import { EM_DASH } from '../lib/typography';
import { Component, Fragment } from 'preact';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, BG_ATMOSPHERE, VIGNETTE,
  KnotOrnament, HeadingWatermark, GLASS_PANEL_SHEER_STYLE, TAB_ACTIVE_TEXT, TAB_INACTIVE, GLASS_TAB_BAR_STYLE,
} from '../lib/medievalTheme';
import { TabBar } from './ui/TabBar';
import { CloseButton } from './ui/navButtons';
import ElementIcon from './deckStats/ElementIcon';
import DiscordHandlePill from './ui/DiscordHandlePill';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { getHallOfFame } from '../utils/arena/hallOfFameApi';
import { getRankedSeasonChampions } from '../utils/arena/rankedSeasonApi';
import { MEDAL_COLORS } from '../utils/arena/trophies';
import { playUI, UI } from '../utils/arena/uiSounds';
import { formatRank } from '../utils/arena/rankUtils';
import { levelFromXp } from '../utils/arena/profileDefaults';
import { k, t } from '../localization/t';

const ELEMENTS = [k('Earth'), k('Water'), k('Fire'), k('Air')];

const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

export default class HallOfFameScreen extends Component {
  state = {
    loading: true,
    error: null,
    data: null,
    champions: null,
    activeTab: 'all',
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [data, championsResult] = await Promise.all([
        getHallOfFame(),
        getRankedSeasonChampions({ limit: 30 }),
      ]);
      if (this._unmounted) return;
      this.setState({
        data,
        champions: championsResult?.seasons || [],
        loading: false,
      });
    } catch (err) {
      console.error('[HallOfFameScreen] Failed to load hall of fame:', err);
      if (this._unmounted) return;
      this.setState({ error: t('Failed to load Hall of Fame.'), loading: false });
    }
  };

  componentWillUnmount() {
    this._unmounted = true;
  }

  setTab = (tab) => {
    if (this.state.activeTab === tab) return;
    playUI(UI.CLICK);
    this.setState({ activeTab: tab });
  };

  getAvatarCatalog() {
    const { sorceryCards } = this.props;
    if (!Array.isArray(sorceryCards)) return [];
    return sorceryCards
      .filter((c) => c.type === 'Avatar')
      .map((c) => ({
        id: c.unique_id,
        name: c.name || c.unique_id,
        image: c.printings?.[0]?.image_url || null,
        elements: Array.isArray(c.elements)
          ? c.elements.map((e) => e?.name).filter((n) => ELEMENTS.includes(n))
          : [],
      }));
  }

  buildAllRows() {
    const { data } = this.state;
    const catalog = this.getAvatarCatalog();
    const statsById = new Map((data?.avatars || []).map((a) => [a.avatarId, a]));
    const rows = catalog.map((meta) => buildRow(meta, statsById.get(meta.id)));
    // Fallback for played avatars missing from the local card db (server/catalog skew).
    for (const stats of data?.avatars || []) {
      if (!catalog.some((c) => c.id === stats.avatarId)) {
        rows.push(buildRow({ id: stats.avatarId, name: stats.avatarId, image: null, elements: [] }, stats));
      }
    }
    return sortRows(rows);
  }

  buildElementRows(element) {
    const { data } = this.state;
    const catalog = this.getAvatarCatalog();
    const catalogById = new Map(catalog.map((c) => [c.id, c]));
    const serverList = data?.byElement?.[element] || [];
    const rows = serverList.map((stats) => {
      const meta = catalogById.get(stats.avatarId) || { id: stats.avatarId, name: stats.avatarId, image: null, elements: [] };
      return buildRow(meta, stats);
    });
    return sortRows(rows);
  }

  renderBody() {
    const { loading, error, data, activeTab } = this.state;
    const { sorceryCards, onViewProfile } = this.props;
    if (loading) {
      return (
        <div className="text-center py-16 text-[0.875cqw]" style={{ color: TEXT_MUTED }}>
          {t('Loading…')}
        </div>
      );
    }
    if (error) {
      return (
        <div className="text-center py-16 text-[0.875cqw]" style={{ color: '#c45050' }}>
          {error}
        </div>
      );
    }
    if (activeTab === 'all') {
      return (
        <AllTabBody
          data={data}
          rows={this.buildAllRows()}
          sorceryCards={sorceryCards}
          onViewProfile={onViewProfile}
          onPickElement={this.setTab}
        />
      );
    }
    if (activeTab === 'champions') {
      return (
        <ChampionsTabBody
          champions={this.state.champions || []}
          onViewProfile={onViewProfile}
        />
      );
    }
    return (
      <ElementTabBody
        element={activeTab}
        rows={this.buildElementRows(activeTab)}
        summary={data?.elements?.[activeTab]}
        sorceryCards={sorceryCards}
        onViewProfile={onViewProfile}
      />
    );
  }

  render() {
    const { onClose } = this.props;
    const { data, activeTab } = this.state;

    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col"
        style={{ background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}` }}
      >
        <HeadingWatermark />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">
          <div className="relative shrink-0 flex items-center justify-center pt-10 pb-4 px-12">
            <div className="relative flex items-center justify-center" aria-hidden="true">
              <div className="flex items-center gap-8">
                <KnotOrnament side="left" size={32} />
                <div className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }} />
                <h1
                  className="arena-heading font-bold"
                  style={{
                    color: TEXT_PRIMARY,
                    fontSize: '2.25cqw',
                    textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.3)',
                  }}
                >
                  {t('Hall of Fame')}
                </h1>
                <div className="h-px w-24" style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }} />
                <KnotOrnament side="right" size={32} />
              </div>
            </div>
            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onClose} />
            </div>
            {data?.totalMatches != null ? (
              <div
                className="absolute left-12 top-10 text-[0.625cqw] font-semibold"
                style={{ color: TEXT_MUTED }}
              >
                {t('{n} matches counted', { n: data.totalMatches.toLocaleString() })}
              </div>
            ) : null}
          </div>

          <div className="shrink-0 px-12 pb-4 flex justify-center">
            <TabBar
              className="inline-flex items-center gap-0.5"
              style={GLASS_TAB_BAR_STYLE}
              activeIndex={['all', ...ELEMENTS, 'champions'].indexOf(activeTab)}
            >
              <TabButton
                active={activeTab === 'all'}
                onClick={() => this.setTab('all')}
                label={t('All Avatars')}
              />
              {ELEMENTS.map((el) => (
                <TabButton
                  key={el}
                  active={activeTab === el}
                  onClick={() => this.setTab(el)}
                  label={t(el)}
                  icon={<ElementIcon element={el} size={14} />}
                />
              ))}
              <TabButton
                active={activeTab === 'champions'}
                onClick={() => this.setTab('champions')}
                label={t('Champions')}
              />
            </TabBar>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-12 pb-12">
            {this.renderBody()}
          </div>
        </div>
      </div>
    );
  }
}

function TabButton({ active, onClick, label, icon }) {
  return (
    <button
      type="button"
      data-tab-item
      className="px-4 py-2 inline-flex items-center gap-2 text-[0.75cqw] font-semibold cursor-pointer"
      style={active ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function AllTabBody({ data, rows, sorceryCards, onViewProfile, onPickElement }) {
  return (
    <div className="flex flex-col gap-6 max-w-[80cqw] mx-auto">
      <div className="grid grid-cols-4 gap-3">
        {ELEMENTS.map((el) => {
          const s = data?.elements?.[el] || { games: 0, wins: 0, losses: 0 };
          return (
            <ElementSummaryCard
              key={el}
              element={el}
              games={s.games}
              wins={s.wins}
              onClick={() => onPickElement(el)}
            />
          );
        })}
      </div>

      <AvatarList rows={rows} sorceryCards={sorceryCards} onViewProfile={onViewProfile} />
    </div>
  );
}

function ElementTabBody({ element, rows, summary, sorceryCards, onViewProfile }) {
  const games = summary?.games || 0;
  const wins = summary?.wins || 0;
  const winRate = games > 0 ? Math.round((wins / games) * 100) : 0;
  const winRateColor = pickWinRateColor(winRate);
  return (
    <div className="flex flex-col gap-6 max-w-[80cqw] mx-auto">
      <div
        className="flex items-center justify-between gap-6 px-6 py-4"
        style={GLASS_PANEL_SHEER_STYLE}
      >
        <div className="flex items-center gap-4">
          <ElementIcon element={element} size={36} />
          <div className="flex flex-col">
            <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Element')}</span>
            <span className="text-[1.25cqw] font-bold" style={{ color: TEXT_PRIMARY }}>{t(element)}</span>
          </div>
        </div>
        <div className="flex items-center gap-8">
          <Stat label={t('Matches')} value={games.toLocaleString()} />
          <Stat label={t('Wins')} value={wins.toLocaleString()} />
          <Stat label={t('Win Rate')} value={`${winRate}%`} valueColor={winRateColor} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-12 text-[0.875cqw]" style={{ color: TEXT_MUTED }}>
          {t('No matches recorded with {element} yet.', { element: t(element) })}
        </div>
      ) : (
        <AvatarList rows={rows} sorceryCards={sorceryCards} onViewProfile={onViewProfile} />
      )}
    </div>
  );
}

function ChampionsTabBody({ champions, onViewProfile }) {
  if (champions.length === 0) {
    return (
      <div
        className="text-center py-12 text-[0.875cqw] max-w-[60cqw] mx-auto"
        style={{ ...GLASS_PANEL_SHEER_STYLE, color: TEXT_MUTED }}
      >
        {t("No seasons have been crowned yet. The first season's champions will appear here when it ends.")}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4 max-w-[64cqw] mx-auto">
      {champions.map((season) => (
        <ChampionsSeasonCard key={season.seasonId} season={season} onViewProfile={onViewProfile} />
      ))}
    </div>
  );
}

function ChampionsSeasonCard({ season, onViewProfile }) {
  return (
    <div
      className="flex flex-col gap-3 px-5 py-4"
      style={GLASS_PANEL_SHEER_STYLE}
    >
      <div className="flex items-center justify-between">
        <span className="text-[0.875cqw] font-bold" style={{ color: TEXT_PRIMARY }}>
          {t('Season {n}', { n: season.seasonId })}
        </span>
        <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
          {season.ranks.length === 1 ? t('1 champion') : t('{n} champions', { n: season.ranks.length })}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((rank) => {
          const entry = season.ranks.find((r) => r.rank === rank);
          return <ChampionSlot key={rank} rank={rank} entry={entry} onViewProfile={onViewProfile} />;
        })}
      </div>
    </div>
  );
}

function ChampionSlot({ rank, entry, onViewProfile }) {
  const [light, mid, dark] = MEDAL_COLORS[rank];
  if (!entry) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2 rounded opacity-50"
        style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid rgba(166,160,155,0.12)` }}
      >
        <div
          className="shrink-0 flex items-center justify-center font-bold"
          style={{
            width: '1.75cqw', height: '1.75cqw', borderRadius: '50%',
            background: `radial-gradient(circle at 35% 30%, ${light} 0%, ${mid} 55%, ${dark} 100%)`,
            color: 'rgba(30,22,8,0.9)', fontSize: '0.75cqw',
          }}
        >
          {rank}
        </div>
        <span className="text-[0.75cqw]" style={{ color: TEXT_MUTED }}>{t('Not awarded')}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        if (onViewProfile) {
          playUI(UI.CLICK);
          onViewProfile(entry.playerId);
        }
      }}
      className="flex items-center gap-3 px-3 py-2 rounded text-left transition-all cursor-pointer"
      style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${GOLD} 0.18)` }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.55)`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.18)`; }}
    >
      <div
        className="shrink-0 flex items-center justify-center font-bold"
        style={{
          width: '1.75cqw', height: '1.75cqw', borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, ${light} 0%, ${mid} 55%, ${dark} 100%)`,
          boxShadow: `inset 0 1px 1px rgba(255,255,255,0.35), inset 0 -1px 2px rgba(0,0,0,0.35), 0 0 8px rgba(212,168,67,${rank === 1 ? 0.45 : 0.18}), 0 1px 2px rgba(0,0,0,0.5)`,
          color: 'rgba(30,22,8,0.9)', fontSize: '0.75cqw',
          textShadow: '0 1px 0 rgba(255,255,255,0.25)',
        }}
      >
        {rank}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate text-[0.875cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
          {entry.name}
        </span>
        <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
          {t('{points} valor · {wins}W {losses}L', { points: entry.points > 0 ? `+${entry.points}` : entry.points, wins: entry.wins, losses: entry.losses })}
        </span>
      </div>
    </button>
  );
}

function ElementSummaryCard({ element, games, wins, onClick }) {
  const rate = games > 0 ? Math.round((wins / games) * 100) : 0;
  const tinted = games > 0;
  let rateColor = TEXT_MUTED;
  if (tinted) rateColor = rate >= 50 ? '#6ee7a0' : '#f4e4b8';
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 transition-all cursor-pointer text-left"
      style={GLASS_PANEL_SHEER_STYLE}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.6)`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.3)`; }}
    >
      <ElementIcon element={element} size={28} />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t(element)}</span>
        <span
          className="text-[1.125cqw] font-bold tabular-nums"
          style={{ color: rateColor }}
        >
          {tinted ? `${rate}%` : EM_DASH}
        </span>
        <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          {games === 1 ? t('1 match') : t('{n} matches', { n: games.toLocaleString() })}
        </span>
      </div>
    </button>
  );
}

function AvatarList({ rows, sorceryCards, onViewProfile }) {
  const unplayedStart = rows.findIndex((r) => r.games === 0);
  const unplayedCount = unplayedStart === -1 ? 0 : rows.length - unplayedStart;
  return (
    <div
      className="flex flex-col"
      style={{ ...GLASS_PANEL_SHEER_STYLE, overflow: 'hidden' }}
    >
      {rows.map((row, i) => (
        <Fragment key={row.id}>
          {i === unplayedStart ? <UnplayedDivider count={unplayedCount} /> : null}
          <AvatarRow
            row={row}
            rank={i + 1}
            firstInSection={i === 0 || i === unplayedStart}
            sorceryCards={sorceryCards}
            onViewProfile={onViewProfile}
          />
        </Fragment>
      ))}
    </div>
  );
}

function UnplayedDivider({ count }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-2"
      style={{
        background: 'rgba(0,0,0,0.35)',
        borderTop: `1px solid ${GOLD} 0.18)`,
        borderBottom: `1px solid ${GOLD} 0.1)`,
      }}
    >
      <span className="text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
        {t('Not yet played')}
      </span>
      <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
        {count === 1 ? t('1 avatar') : t('{n} avatars', { n: count })}
      </span>
    </div>
  );
}

function AvatarRow({ row, rank, firstInSection, sorceryCards, onViewProfile }) {
  const { name, image, elements, games, wins, losses, winRate, topPlayer } = row;
  const ratePct = Math.round(winRate * 100);
  const hasGames = games > 0;
  let rateColor = TEXT_MUTED;
  if (hasGames) rateColor = ratePct >= 50 ? '#6ee7a0' : '#f4e4b8';
  return (
    <div
      className="grid items-start gap-3 px-4 py-3"
      style={{
        gridTemplateColumns: '2.5cqw 4cqw 1fr 9cqw 7cqw 16cqw',
        borderTop: firstInSection ? 'none' : `1px solid ${GOLD} 0.08)`,
        opacity: hasGames ? 1 : 0.5,
      }}
    >
      <span className="text-[0.75cqw] font-bold tabular-nums" style={{ color: TEXT_MUTED }}>
        {hasGames ? `#${rank}` : EM_DASH}
      </span>
      <div
        className="overflow-hidden rounded-card"
        style={{
          width: '3.75cqw',
          aspectRatio: '63 / 88',
          background: 'rgba(0,0,0,0.4)',
          border: `1px solid ${GOLD} 0.15)`,
        }}
      >
        {image ? (
          <img
            src={image}
            alt={name}
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: hasGames ? 'none' : 'grayscale(1)',
            }}
          />
        ) : null}
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[0.875cqw] font-semibold truncate" style={{ color: hasGames ? TEXT_PRIMARY : TEXT_BODY }}>
          {name}
        </span>
        {elements.length > 0 ? (
          <div className="flex items-center gap-1 mt-1" style={{ filter: hasGames ? 'none' : 'grayscale(1)' }}>
            {elements.map((el) => <ElementIcon key={el} element={el} size={12} />)}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col tabular-nums">
        <ColumnHeader>{t('Record')}</ColumnHeader>
        <span className="text-[0.875cqw] font-semibold" style={{ color: hasGames ? TEXT_BODY : TEXT_MUTED }}>
          {hasGames ? `${wins}-${losses}` : EM_DASH}
        </span>
        <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          {games === 1 ? t('1 match') : t('{n} matches', { n: games.toLocaleString() })}
        </span>
      </div>
      <div className="flex flex-col tabular-nums">
        <ColumnHeader>{t('Win Rate')}</ColumnHeader>
        <span
          className="text-[1.125cqw] font-bold"
          style={{ color: rateColor, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
          {hasGames ? `${ratePct}%` : EM_DASH}
        </span>
      </div>
      <div className="flex flex-col min-w-0">
        <ColumnHeader>{t('Top Player')}</ColumnHeader>
        <TopPlayer topPlayer={topPlayer} sorceryCards={sorceryCards} onViewProfile={onViewProfile} />
      </div>
    </div>
  );
}

function ColumnHeader({ children }) {
  return (
    <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
      {children}
    </span>
  );
}

// Clickable ghost button. Avatar art on the left, name + Discord handle
// in the top row with the rank/level meta and wins count stacked
// underneath. Clicking opens the player's profile via the onViewProfile
// handler the leaderboard rows use. The Discord pill stops propagation
// so copying the handle doesn't also fire the profile click.
function TopPlayer({ topPlayer, sorceryCards, onViewProfile }) {
  if (!topPlayer?.name) {
    return <span className="text-[0.875cqw]" style={{ color: TEXT_MUTED }}>...</span>;
  }
  const avatarUrl = resolveAvatarUrl(topPlayer, sorceryCards);
  const handleClick = () => {
    if (topPlayer.id && typeof onViewProfile === 'function') {
      playUI(UI.CLICK);
      onViewProfile(topPlayer.id);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md w-full text-left transition-all cursor-pointer"
      style={{
        background: 'transparent',
        border: `1px solid transparent`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `${GOLD} 0.08)`;
        e.currentTarget.style.borderColor = `${GOLD} 0.3)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <div
        className="shrink-0 overflow-hidden rounded-full"
        style={{
          width: '2.5cqw',
          height: '2.5cqw',
          background: `${GOLD} 0.08)`,
          border: `1px solid ${GOLD} 0.2)`,
        }}
      >
        {avatarUrl ? (
          <AvatarImage src={avatarUrl} className="w-full h-full" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-[0.75cqw] font-semibold"
            style={{ color: TEXT_MUTED }}
          >
            {topPlayer.name[0]?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1 gap-0.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[0.875cqw] font-semibold truncate min-w-0 flex-1" style={{ color: ACCENT_GOLD }}>
            {topPlayer.name}
          </span>
          {topPlayer.discordHandle ? (
            <DiscordHandlePill
              handle={topPlayer.discordHandle}
              size="sm"
              withAt
              chrome="text"
              className="shrink-0"
            />
          ) : null}
        </div>
        <PlayerRankLine tier={topPlayer.tier} division={topPlayer.division} xp={topPlayer.xp} />
        <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
          {topPlayer.wins === 1 ? t('1 win') : t('{n} wins', { n: topPlayer.wins })}
        </span>
      </div>
    </button>
  );
}

function MetaSep() {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 rounded-full"
      style={{
        width: '0.18cqw',
        height: '0.18cqw',
        background: 'rgba(212,168,67,0.4)',
      }}
    />
  );
}

function PlayerRankLine({ tier, division, xp }) {
  const level = levelFromXp(xp || 0);
  const isRanked = !!tier;
  const tierHex = isRanked ? (TIER_HEX[tier] || TEXT_BODY) : '#A6A09B';
  return (
    <div
      className="flex flex-wrap items-center min-w-0"
      style={{ columnGap: '0.55cqw', rowGap: '0.15cqw' }}
    >
      <span
        className="shrink-0 text-[0.5625cqw] font-bold"
        style={{ color: TEXT_MUTED, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
      >
        {t('Lvl {n}', { n: level })}
      </span>
      <MetaSep />
      <span
        className="shrink-0 inline-flex items-center gap-1 text-[0.5625cqw] font-bold"
        style={{ color: tierHex, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
      >
        <span
          aria-hidden="true"
          className="rounded-full"
          style={{
            width: '0.375cqw',
            height: '0.375cqw',
            background: tierHex,
            boxShadow: `0 0 4px ${tierHex}`,
          }}
        />
        {isRanked ? formatRank(tier, division) : t('Unranked')}
      </span>
    </div>
  );
}

function Stat({ label, value, valueColor = TEXT_PRIMARY }) {
  return (
    <div className="flex flex-col items-end tabular-nums">
      <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{label}</span>
      <span className="text-[1.125cqw] font-bold" style={{ color: valueColor }}>{value}</span>
    </div>
  );
}

function pickWinRateColor(ratePct) {
  if (ratePct >= 50) return '#6ee7a0';
  if (ratePct > 0) return '#f4e4b8';
  return TEXT_MUTED;
}

function buildRow(meta, stats) {
  const games = stats?.games || 0;
  const wins = stats?.wins || 0;
  const losses = stats?.losses || 0;
  return {
    id: meta.id,
    name: meta.name,
    image: meta.image,
    elements: meta.elements,
    games,
    wins,
    losses,
    winRate: games > 0 ? wins / games : 0,
    topPlayer: stats?.topPlayer || null,
  };
}

// Played avatars first (win rate desc, games desc, name asc); unplayed at the bottom alphabetically.
function sortRows(rows) {
  return [...rows].sort((a, b) => {
    if (a.games === 0 && b.games === 0) return a.name.localeCompare(b.name);
    if (a.games === 0) return 1;
    if (b.games === 0) return -1;
    if (b.winRate !== a.winRate) return b.winRate - a.winRate;
    if (b.games !== a.games) return b.games - a.games;
    return a.name.localeCompare(b.name);
  });
}
